import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import Tabs from 'antd/es/tabs';

import useNotifications from 'hooks/useNotifications';
import useQueryParam from 'hooks/useQueryParam';

import MainLayout from 'layouts/Main.layout';
import MainPageHeader from 'components/MainPageHeader';
import { useFeatureToggleItems } from 'components/FeatureToggle';
import { Row, Col, Space } from 'components/layout';
import Button from 'components/Button';
import DropdownSelect from 'components/DropdownSelect';
import NotificationsList from 'components/notifications/NotificationsList';

import { NOTIFICATION_FILTER_OPTIONS } from '../constants';
import featureToggles from 'config/featureToggles';

const NotificationsPage = () => {
  const notificationTypeToggles = useFeatureToggleItems(featureToggles.NOTIFICATIONS);

  const [activeTab, setActiveTab] = useState('active');
  const notifications = useNotifications({autoFetch: true, refreshInterval: 180000});
  const [notificationType, setNotificationType] = useQueryParam('type', '');

  const notificationTypeFilterOptions = useMemo(() => {
    return NOTIFICATION_FILTER_OPTIONS.map(filterOptionGroup => {
      filterOptionGroup = { ...filterOptionGroup };
      filterOptionGroup.items = filterOptionGroup.items.filter(item => {
        const notificationTypeToggle = notificationTypeToggles.find(toggle => {
          return toggle.itemName === item.value;
        });
  
        if (notificationTypeToggle) {
          if (notificationTypeToggle.visible === false) {
            return false;
          }
        }
        return true;
      });
      return filterOptionGroup;
    });
  }, [notificationTypeToggles]);

  useEffect(() => {
    notifications.actions.getActive();
    notifications.actions.getPast();
  }, [notifications.actions]);

  const readActiveHandler = useCallback(() => {
    notifications.actions.markAsReadByType({ notification_type: notificationType });
  }, [notifications.actions, notificationType]);

  const notificationsData = useMemo(() => {
    let notificationsData = notifications.state[activeTab] || [];
    if (notificationType) {
      notificationsData = notificationsData.filter(n => n.notification_type === notificationType);
    }
    return notificationsData;
  }, [notifications.state, notificationType, activeTab]);

  const hasUnread = notificationsData.filter(n => n.is_read === 0).length > 0;

  return (
    <MainLayout>
      <MainPageHeader
        title="Alerts"
        icon="bell"
      />
      <Row style={{ padding: '24px', paddingTop: 0 }}>
        <Col span={24}>
          <Row justify="space-between" align="middle">
            <NotificationTabs
              activeKey={activeTab}
              onChange={key => setActiveTab(key)}
            >
              <Tabs.TabPane key="active" tab="Active" />
              <Tabs.TabPane key="past" tab="Past" />
            </NotificationTabs>
            <Space>
              <DropdownSelect
                icon={{ name: 'filter', size: 16, color: '#A4B0BA' }}
                options={notificationTypeFilterOptions}
                value={notificationType}
                onChange={setNotificationType}
                onRemove={() => setNotificationType('')}
                allowRemove={true}
                showSelected={true}
                showSelectedGroup={false}
                selectedAlign="start"
              />
              {activeTab === 'active' && (
                <Button
                  type="link"
                  icon={{
                    name: 'check',
                    size: 16,
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={readActiveHandler}
                  disabled={!hasUnread}
                >
                  Mark all as read
                </Button>
              )}
            </Space>
          </Row>

        </Col>
        <Col span={24}>
          <StyledNotificationList
            data={notificationsData}
            loading={notifications.state.isLoading}
            pagination={true}
            onItemClicked={item => {
              if (!item.is_read) {
                notifications.actions.markAsRead(item);
              }
            }}
          />
        </Col>
      </Row>

    </MainLayout>
  );
};

const NotificationTabs = styled(Tabs)`
  & .ant-tabs-tab {
    padding: 16px;
    margin: 0 16px 0 0;
    font-size: 15px;
  }

  & .ant-tabs-nav {
    margin-bottom: 0;
  }

  & .ant-tabs-nav::before {
    border-bottom: none;
  }

  & .ant-tabs-content-holder {
    display: none;
  }
`;

const StyledNotificationList = styled(NotificationsList)`
  & .ant-list-empty-text {
    box-shadow: unset;
  }
`;

export default NotificationsPage;
